<template>
  <div class="q-pa-md">
    <div class="row">
      <q-select
        class="q-ma-xs col-4"
        label="Sessions"
        v-model="sessionsDropDown"
        :options="sessionOptions"
        multiple
        use-chips
        @update:model-value="fetchApplicationsBySessions"
        dense
        emit-value
        map-options
      >
        <template v-slot:selected-item="scope">
          <q-chip
            class="q-ma-xs"
            removable
            dense
            @remove="scope.removeAtIndex(scope.index)"
            @update:model-value="allSessionsSelected = false"
          >
            {{ scope.opt.label }}
          </q-chip>
        </template>
        <template v-slot:option="{ itemProps, opt, selected, toggleOption }">
          <q-item v-bind="itemProps">
            <q-item-section>
              <q-item-label v-html="opt.label" />
            </q-item-section>
            <q-item-section side>
              <q-toggle :model-value="selected" @update:model-value="toggleOption(opt)" />
            </q-item-section>
          </q-item>
        </template>
      </q-select>
      <q-space />
      <q-btn
        class="q-my-sm q-mr-xs"
        color="primary"
        @click="selectPlacementFilters"
        label="Placement Filters"
        no-caps
      />
      <q-btn
        class="q-my-sm q-mr-xs"
        color="primary"
        @click="selectAccountingFilters"
        label="Accounting Filters"
        no-caps
      />
    </div>
    <q-table
      title="Applications"
      :rows="applications"
      :columns="columns"
      row-key="applicationId"
      :visible-columns="visibleColumns"
      v-model:pagination="pagination"
      :rows-per-page-options="[10, 25, 50, 100, 200]"
      @request="onRequest"
      :no-data-label="sessionOptions.length === 0 ?
      'No open sessions exist, please create a new session'
      : 'No applications found within given search criteria'"
      binary-state-sort
    >
      <template v-slot:top-right>
        <q-input
          v-model.trim="filterModel"
          placeholder="Search"
          class="q-ma-sm col-2"
          @keydown.enter="onRequest"
          dense
        >
          <template v-slot:append>
            <q-icon name="search" />
          </template>
        </q-input>
        <q-select
          class="q-mr-xs"
          v-model="visibleColumns"
          multiple
          outlined
          dense
          options-dense
          display-value=""
          emit-value
          map-options
          :options="columnOptions"
          placeholder="Filter"
          option-value="name"
          input-debounce="0"
          use-input
          @filter="filterFn"
          style="min-width: 150px"
        >
          <template #before-options
              v-if="columns.length == columnOptions.length">
            <q-item
              clickable
              @click="allColumnsSelected = !allColumnsSelected; checkAll(allColumnsSelected)">
            <q-item-section side>
              <q-checkbox
                v-model="allColumnsSelected"
                @update:model-value="(val) => checkAll(val)"
              />
              </q-item-section>

              <q-item-section>
                Select All
              </q-item-section>
            </q-item>
          </template>
          <template #option="scope">
            <q-item
              clickable
              @click="scope.toggleOption(scope.opt)"
            >
              <q-item-section side>
                <q-checkbox
                  :model-value="scope.selected"
                  @click="scope.toggleOption(scope.opt)"
                />
              </q-item-section>

              <q-item-section>
                {{ scope.opt.name }}
              </q-item-section>
            </q-item>
          </template>
        </q-select>
        <q-btn
          class="q-mr-xs"
          color="primary"
          icon-right="sync"
          no-caps
          @click="onUpdateToDigiPenEmailsAndEapStatus()"
        >
          <q-tooltip class="text-body2">
            Update all emails to digipen.edu format and
            update applied to EAP status.
          </q-tooltip>
        </q-btn>
        <q-uploader
          ref="applicationUploader"
          :url="applicationImportUrl"
          max-files="1"
          accept=".csv"
          max-file-size=20971520
          auto-upload
          style="display:none;"
          @start="q.loading.show();"
          @uploaded="uploadedApplicationImportData"
          @failed="(info) => uploadDataFailed(info)"
        />
        <q-btn
          class="q-mr-xs"
          color="primary"
          icon-right="unarchive"
          no-caps
          @click="importApplications"
        >
         <q-tooltip class="text-body2">
            Import CSV file and Update Housing Portal applications with it.
          </q-tooltip>
        </q-btn>
        <q-btn
          class="q-mr-xs"
          color="primary"
          icon-right="archive"
          no-caps
          @click="exportApplications"
        >
          <q-tooltip class="text-body2">
            Export applications to a CSV file.
          </q-tooltip>
        </q-btn>
        <q-btn
          class="q-mr-xs"
          color="primary"
          icon-right="mail"
          no-caps
          @click="openEmailPreview()"
        >
         <q-tooltip class="text-body2">
            Send manual emails.
          </q-tooltip>
        </q-btn>
      </template>
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
          >
            {{ col.label }}
            <q-btn
              flat
              dense
              size="sm"
              icon="filter_alt"
              :color="(columnFilterDict[col.field] && columnFilterDict[col.field]?.length !== 0)
              ? 'primary' : 'grey'"
              class="q-ml-xs"
              @click.stop="showColumnFilter = true"
            >
              <q-space />
              <q-popup-edit
                v-model="columnFilterDict[col.field]"
                buttons
                v-slot="scope"
                @before-show="lazyLoadColumnFilterValues(col.field)"
                @save="(val) => { applyColumnFilter(val, col.field) }"
                anchor="top left"
                max-width="377px"
                >
                <q-select
                  v-model="scope.value"
                  multiple
                  use-chips
                  use-input
                  input-debounce="0"
                  @filter="columnFilter"
                  :options="columnFilterOptions"
                  @keyup.enter="scope.set"
                />
              </q-popup-edit>
            </q-btn>
          </q-th>
          <q-th v-if="hasRoles('R&D', 'HRL', 'ACC')">
            Edit
          </q-th>
          <q-th>
            View
          </q-th>
          <q-th>
            Logs
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr
          :props="props"
          :class="{ 'under-18-row': props.row.isUnder18, 'over-25-row': props.row.is25OrOlder }"
        >
          <q-td v-for="col in props.cols"
                :key="col.name"
                :props="props">
            <div v-if="col.field === 'digipenEmail' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.digipenEmail"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, col.name)}">
                <q-input v-model="scope.value"
                         :options="props.row.digipenEmail"
                         @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>
            <div v-else-if="col.field === 'status' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.status"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-select v-model="scope.value"
                          :options="props.row.applicationStatuses"
                          @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>

            <div v-else-if="col.field === 'waitlistStatus' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.waitlistStatus"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-select v-model="scope.value"
                          :options="props.row.waitlistStatuses"
                          @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>

            <div v-else-if="col.field === 'residencyStatus' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.residencyStatus"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-select v-model="scope.value"
                          map-options
                          emit-value
                          :options="props.row.residencyStatuses"
                          @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>

              <div v-else-if="col.field === 'moveInGroup' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.moveInGroup"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-select v-model="scope.value"
                          map-options
                          emit-value
                          :options="props.row.moveInGroups"
                          @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>

            <div v-else-if="col.field === 'dssAccessible' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.dssAccessible"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-select v-model="scope.value"
                          :options="['Waived', 'Requested', 'Approved', 'Received']"
                          @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>

            <div v-else-if="col.field === 'proxies'">
              {{ col.value.length > 0 ? 'Yes' : 'No' }}
            </div>

            <div v-else-if="col.field === 'returningForNextSession' && hasRoles('R&D', 'HRL')">
              <q-popup-edit v-model="props.row.returningForNextSession"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-select v-model="scope.value"
                          :options="['Yes', 'No', 'Undecided']"
                          @keyup.enter="scope.set" />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>

            <div v-else-if="col.field === 'notes' && hasRoles('R&D', 'HRL')"
                 class="hover-over">
              <q-popup-edit v-model="props.row.notes"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-input type="textarea"
                         v-model="scope.value"
                         autofocus />
              </q-popup-edit>
              <span v-if="col.value === null">
                &nbsp;
              </span>
              <span v-else>
                {{ col.value }}
              </span>
            </div>
            <div v-else-if="col.field === 'depositRefundAmount' && hasRoles('R&D', 'HRL', 'ACC')">
              <q-popup-edit v-model="props.row.depositRefundAmount"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-input v-model="scope.value"
                         dense
                         autofocus
                         counter />
              </q-popup-edit>
              <div class="hover-over">{{ col.value }}</div>
            </div>
            <div v-else-if="col.field === 'depositRefundNotes' && hasRoles('R&D', 'HRL', 'ACC')"
                 class="hover-over">
              <q-popup-edit v-model="props.row.depositRefundNotes"
                            buttons
                            v-slot="scope"
                            @save="(val) => {submitInputFromColumn(val, props.row, col.name)}">
                <q-input type="textarea"
                         v-model="scope.value"
                         autofocus />
              </q-popup-edit>
              <span v-if="col.value === null">
                &nbsp;
              </span>
              <span v-else>
                {{ col.value }}
              </span>
            </div>
            <div v-else-if="col.field === 'agreement'">
              <q-checkbox v-if="hasRoles('R&D', 'HRL')"
                          v-model="props.row.agreement"
                          :label="$filters.receivedPending(props.row.agreement)"
                          size="xs"
                          color="green"
                          @update:model-value="submitInputFromColumn(
                props.row.agreement, props.row, col.name)" />
              <div v-else>
                {{ $filters.receivedPending(props.row.agreement) }}
              </div>
            </div>
            <div v-else-if="col.field === 'handbookAgreement'">
              <q-checkbox v-if="hasRoles('R&D', 'HRL')"
                          v-model="props.row.handbookAgreement"
                          :label="$filters.receivedPending(props.row.handbookAgreement)"
                          size="xs"
                          color="green"
                          @update:model-value="submitInputFromColumn(
                props.row.handbookAgreement, props.row, col.name)" />
              <div v-else>
                {{ $filters.receivedPending(props.row.handbookAgreement) }}
              </div>
            </div>
            <div v-else-if="col.field === 'hasAppliedToEap'">
              <div>
                {{ $filters.yesNo(props.row.hasAppliedToEap) }}
              </div>
            </div>
            <div v-else-if="col.field === 'stayInSameUnit'">
              <div>
                {{ $filters.yesNo(props.row.stayInSameUnit) }}
              </div>
            </div>
            <div v-else-if="col.field === 'isReturningToHousing'">
              <div>
                {{ $filters.yesNo(props.row.isReturningToHousing) }}
              </div>
            </div>
            <div v-else-if="col.field === 'roommatesClean'">
              <div>
                {{ $filters.yesNo(props.row.roommatesClean) }}
              </div>
            </div>
            <div v-else-if="col.field === 'roommatesHaveSameDegree'">
              <div>
                {{ $filters.yesNo(props.row.roommatesHaveSameDegree) }}
              </div>
            </div>
            <div v-else-if="col.field === 'roommatesHaveEmotionalSupportAnimal'">
              <div>
                {{ $filters.yesNo(props.row.roommatesHaveEmotionalSupportAnimal) }}
              </div>
            </div>
            <div v-else-if="col.field === 'roommatesShare'">
              <div>
                {{ $filters.yesNo(props.row.roommatesShare) }}
              </div>
            </div>
            <div v-else-if="col.field === 'roommatesSocial'">
              <div>
                {{ $filters.yesNo(props.row.roommatesSocial) }}
              </div>
            </div>
            <div v-else-if="col.field === 'hasPetAllergies'">
              <div>
                {{ $filters.yesNo(props.row.hasPetAllergies) }}
              </div>
            </div>
            <div v-else-if="col.field === 'animalInPlacement'">
              <div>
                {{ $filters.yesNo(props.row.animalInPlacement) }}
              </div>
            </div>
            <div v-else-if="col.field === 'isMorningPerson'">
              <div>
                {{ $filters.yesNo(props.row.isMorningPerson) }}
              </div>
            </div>
            <div v-else-if="col.field === 'isNightPerson'">
              <div>
                {{ $filters.yesNo(props.row.isNightPerson) }}
              </div>
            </div>
            <div v-else-if="col.field === 'cleanlinessHabits'">
              <div>
                {{ $filters.yesNo(props.row.cleanlinessHabits) }}
              </div>
            </div>
            <div v-else-if="col.field === 'sharingHabits'">
              <div>
                {{ $filters.yesNo(props.row.sharingHabits) }}
              </div>
            </div>
            <div v-else-if="col.field === 'socialHabits'">
              <div>
                {{ $filters.yesNo(props.row.socialHabits) }}
              </div>
            </div>
            <div v-else-if="col.field === 'shareEmail'">
              <div>
                {{ $filters.yesNo(props.row.shareEmail) }}
              </div>
            </div>
            <div v-else-if="col.field === 'roomType'">
              <div v-if="props.row.roomName">
                {{ props.row.roomType }}
              </div>
            </div>
            <div v-else-if="col.field === 'nsoFeeEligible'">
              <div>
                {{ col.value || 'N/A' }}
              </div>
            </div>
            <div v-else-if="col.field === 'seaStatus'">
              <div>
                {{ col.value === true ? 'Yes' : col.value === false ? 'No' : 'N/A' }}
              </div>
            </div>
            <div v-else-if="col.field === 'firstName'">
              {{ col.value }}
              <q-badge
                v-if="props.row.isUnder18"
                color="deep-orange"
                class="q-ml-xs"
                rounded
              >
                Under 18
                <q-tooltip class="text-body2">
                  Student is under 18 as of move-in parental sign-off
                  required on housing agreements and contracts.
                </q-tooltip>
              </q-badge>
              <q-badge
                v-if="props.row.is25OrOlder"
                color="indigo"
                class="q-ml-xs"
                rounded
              >
                25+
                <q-tooltip class="text-body2">
                  Student is 25 or older as of move-in.
                </q-tooltip>
              </q-badge>
            </div>
            <div v-else>
              {{ col.value }}
            </div>
          </q-td>
          <q-td v-if="hasRoles('R&D', 'HRL', 'ACC')">
            <q-btn
              @click="isEditModalActive = true; isReadOnlyAccess = false;
              updateApplicationState(props.row.applicationId)"
              class="q-mr-xs"
              size="sm"
              color="white"
              text-color="primary"
              round
              flat
              icon="edit"
            />
          </q-td>
          <q-td>
            <q-btn
              @click="isEditModalActive = true; isReadOnlyAccess = true;
              updateApplicationState(props.row.applicationId)"
              class="q-mr-xs"
              size="sm"
              color="white"
              text-color="primary"
              round
              flat
              icon="visibility"
            />
          </q-td>
          <q-td>
            <q-btn
              @click="openLogsModal(props.row.applicationId)"
              class="q-mr-xs"
              size="sm"
              color="white"
              text-color="primary"
              round
              flat
              icon="history"
            />
          </q-td>
        </q-tr>
      </template>
    </q-table>
    <q-dialog
      ref="editModal"
      v-model="isEditModalActive"
    >
      <q-card style="width: 800px; max-width: 800px;">
        <ApplicationDetailedSummary
          ref="applicationForm"
          :isReadOnly=isReadOnlyAccess
        />

        <q-card-actions
          align="center"
          class="bg-white text-teal"
        >
          <q-btn
            flat
            label="Cancel"
            v-close-popup
            color="primary"
          />
          <q-btn
            v-show="hasRoles('R&D', 'HRL', 'ACC')"
            flat
            label="Save"
            @click="submitApplicationForm()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog
      ref="emailModal"
      v-model="isEmailModalActive"
    >
      <q-card style="width: 800px; max-width: 80dvw; height: 89vh">
        <EmailPreview
          ref="emailPreview"
          :applicantEmailList=applicantEmailList
          :sessionsDropDown = sessionsDropDown
          @onSuccess="closeEmailModal"
        />
      </q-card>
    </q-dialog>
    <q-dialog
      ref="logsModal"
      v-model="isLogsModalActive"
      persistent
    >
    <ApplicationLogs :applicationId="selectedApplicationForLogs" />
  </q-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed } from 'vue';
import { date, useQuasar } from 'quasar';
import useApplication from '@/hooks/application';
import useAuth from '@/hooks/auth';
import useSession from '@/hooks/session';
import useUtility from '@/hooks/utility';
import useUtilities from '@/utilities/uiUtilities';
import EmailPreview from '@/components/Email/EmailPreview.vue';
import ApplicationDetailedSummary from '../ApplicationForm/ApplicationDetailedSummary.vue';
import ApplicationLogs from './ApplicationLogs.vue';

export default {
  async setup() {
    const { fetchSessionNameIds, sessionNameIds } = useSession();
    const {
      applicationDetails,
      currentApplicationId,
      getApplicantEmails,
      getApplication,
      getApplicationsExport,
      getColumnFilterValues,
      updateApplication,
      updateApplicationByAccounting,
    } = useApplication();
    const $q = useQuasar();
    const { hasRoles } = useAuth();
    const {
      createDownloadableFile,
      isDefaultDateTime,
      notifyResult,
      convertFormattedBooleanColumnToBoolean,
      formatDate,
    } = useUtilities();
    const { updateToDigiPenEmailsAndEapStatus } = useUtility();

    const allSessionsSelected = ref(true);
    const applicationForm = ref();
    const editModal = ref(null);
    const filterModel = ref('');
    const selectedRowId = ref();
    const applicationUploader = ref(null);
    const applicationImportUrl = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/import`;

    const columnFilterValues = ref([]);
    const columnFilterDict = ref({});
    const emailModal = ref();
    const applicantEmailList = ref([]);
    const emailPreview = ref();

    const isLogsModalActive = ref(false);
    const selectedApplicationForLogs = ref(null);
    const logsModal = ref(null);

    const columns = [
      {
        name: 'First Name', align: 'left', label: 'First Name', field: 'firstName', sortable: true,
      },
      {
        name: 'Preferred First Name', align: 'left', label: 'Preferred First Name', field: 'preferredFirstName',
      },
      {
        name: 'Last Name', align: 'left', label: 'Last Name', field: 'lastName', sortable: true,
      },
      {
        name: 'DigiPen Email', align: 'left', label: 'DigiPen Email', field: 'digipenEmail',
      },
      {
        name: 'Colleague ID', align: 'left', label: 'Colleague ID', field: 'colleagueId',
      },
      {
        name: 'Session Name', align: 'left', label: 'Session Name', field: 'sessionName',
      },
      {
        name: 'Academic Year', align: 'left', label: 'Academic Year', field: 'academicYear', sortable: true,
      },
      {
        name: 'Application Status', align: 'left', label: 'Application Status', field: 'status', sortable: true,
      },
      {
        name: 'Waitlist Status', align: 'left', label: 'Waitlist Status', field: 'waitlistStatus', sortable: true,
      },
      {
        name: 'Residency Status', align: 'left', label: 'Residency Status', field: 'residencyStatus',
      },
      {
        name: 'Move In Group', align: 'left', label: 'Move In Group', field: 'moveInGroup', sortable: true,
      },
      {
        name: 'Deposit', align: 'left', label: 'Deposit', field: 'deposit', sortable: true,
      },
      {
        name: 'Vaccine', align: 'left', label: 'Vaccine', field: 'vaccinationStatus', sortable: true,
      },
      {
        name: 'DSS', align: 'left', label: 'DSS', field: 'dssAccessible', sortable: true,
      },
      {
        name: 'Housing Agreement', align: 'left', label: 'Housing Agreement', field: 'agreement', sortable: true,
      },
      {
        name: 'Handbook Agreement', align: 'left', label: 'Handbook Agreement', field: 'handbookAgreement', sortable: true,
      },
      {
        name: 'Notes',
        label: 'Notes',
        align: 'left',
        field: 'notes',
        classes: 'ellipsis',
        style: 'max-width: 200px',
        headerStyle: 'max-width: 200px',
      },
      {
        name: 'Application Accepted Date', align: 'left', label: 'Application Accepted Date', field: 'applicationAccepted', format: (val) => (isDefaultDateTime(val) ? '' : date.formatDate(val, 'MM/DD/YYYY')),
      },
      {
        name: 'Application Created Date', align: 'left', label: 'Application Created Date', field: 'applicationCreated', format: (val) => (isDefaultDateTime(val) ? '' : date.formatDate(val, 'MM/DD/YYYY')),
      },
      {
        name: 'Applied To EAP', align: 'left', label: 'Applied To EAP', field: 'hasAppliedToEap',
      },
      {
        name: 'Apartment Preference 1', align: 'left', label: 'Apartment Preference 1', field: 'apartmentPreference1',
      },
      {
        name: 'Apartment Preference 2', align: 'left', label: 'Apartment Preference 2', field: 'apartmentPreference2',
      },
      {
        name: 'Cleanliness', align: 'left', label: 'Cleanliness', field: 'cleanlinessHabits',
      },
      {
        name: 'Date of Birth', align: 'left', label: 'Date of Birth', field: 'dateOfBirth', format: (val) => date.formatDate(val, 'MM/DD/YYYY'),
      },
      {
        name: 'Degree', align: 'left', label: 'Degree', field: 'degree', sortable: true,
      },
      {
        name: 'Deposit Amount', align: 'left', label: 'Deposit Amount', field: 'depositAmount',
      },
      {
        name: 'Deposit Completed Date', align: 'left', label: 'Deposit Completed Date', field: 'depositCompleted', format: (val) => (isDefaultDateTime(val) ? '' : date.formatDate(val, 'MM/DD/YYYY')),
      },
      {
        name: 'Deposit Refund Amount', align: 'left', label: 'Deposit Refund Amount', field: 'depositRefundAmount',
      },

      {
        name: 'Deposit Refund Notes',
        align: 'left',
        label: 'Deposit Refund Notes',
        field: 'depositRefundNotes',
        classes: 'ellipsis',
        style: 'max-width: 200px',
        headerStyle: 'max-width: 200px',
      },
      {
        name: 'Emergency Contact Email', align: 'left', label: 'Emergency Contact Email', field: 'emergencyContactEmail',
      },
      {
        name: 'Emergency Contact Name', align: 'left', label: 'Emergency Contact Name', field: 'emergencyContactName',
      },
      {
        name: 'Emergency Contact Phone', align: 'right', label: 'Emergency Contact Phone', field: 'emergencyContactPhone',
      },
      {
        name: 'Emergency Contact Relation', align: 'left', label: 'Emergency Contact Relation', field: 'emergencyContactRelation',
      },
      {
        name: 'Gender Identity', align: 'left', label: 'Gender Identity', field: 'genderIdentity',
      },
      {
        name: 'Gender Identity (Other)', align: 'left', label: 'Gender Identity (Other)', field: 'genderIdentityOther',
      },
      {
        name: 'Graduating This Year', align: 'left', label: 'Graduating This Year', field: 'graduatingThisYear', sortable: true,
      },
      {
        name: 'Live With Same Genders', align: 'left', label: 'Live With Same Genders', field: 'liveWithSameGenders',
      },
      {
        name: 'Living Learning Community', align: 'right', label: 'Living Learning Community', field: 'livingLearningCommunityStatus',
      },
      {
        name: 'Mobile Phone Number', align: 'right', label: 'Mobile Phone Number', field: 'mobilePhone',
      },
      {
        name: 'Morning Person', align: 'right', label: 'Morning Person', field: 'isMorningPerson',
      },
      {
        name: 'Night Person', align: 'right', label: 'Night Person', field: 'isNightPerson',
      },
      {
        name: 'Permanent Email', align: 'left', label: 'Permanent Email', field: 'permanentEmail',
      },
      {
        name: 'Permanent Mail Address', align: 'left', label: 'Permanent Mail Address', field: 'permanentAddress',
      },
      {
        name: 'Permanent Mail Address 2', align: 'left', label: 'Permanent Mail Address 2', field: 'permanentAddress2',
      },
      {
        name: 'City', align: 'left', label: 'City', field: 'city',
      },
      {
        name: 'State', align: 'left', label: 'State', field: 'state',
      },
      {
        name: 'Country', align: 'left', label: 'Country', field: 'country',
      },
      {
        name: 'Zip', align: 'left', label: 'Zip', field: 'zip',
      },
      {
        name: 'Permanent Phone Number', align: 'right', label: 'Permanent Phone Number', field: 'permanentPhone',
      },
      {
        name: 'Pet Allergies', align: 'left', label: 'Pet Allergies', field: 'hasPetAllergies',
      },
      {
        name: 'ESA', align: 'left', label: 'Has ESA', field: 'animalInPlacement',
      },
      {
        name: 'Placement Status', align: 'left', label: 'Placement Status', field: 'placementStatus',
      },
      {
        name: 'Placement Trait', align: 'left', label: 'Placement Trait', field: 'placementTrait',
      },
      {
        name: 'Preferred Roommate Digipen Email 1', align: 'left', label: 'Preferred Roommate Digipen Email 1', field: 'preferredRoommateDigipenEmail1',
      },
      {
        name: 'Preferred Roommate Digipen Email 2', align: 'left', label: 'Preferred Roommate Digipen Email 2', field: 'preferredRoommateDigipenEmail2',
      },
      {
        name: 'Preferred Roommate Digipen Email 3', align: 'left', label: 'Preferred Roommate Digipen Email 3', field: 'preferredRoommateDigipenEmail3',
      },
      {
        name: 'Preferred Roommate Digipen Email 4', align: 'left', label: 'Preferred Roommate Digipen Email 4', field: 'preferredRoommateDigipenEmail4',
      },
      {
        name: 'Preferred Roommate Email 1', align: 'left', label: 'Preferred Roommate Email 1', field: 'preferredRoommateEmail1',
      },
      {
        name: 'Preferred Roommate Email 2', align: 'left', label: 'Preferred Roommate Email 2', field: 'preferredRoommateEmail2',
      },
      {
        name: 'Preferred Roommate Email 3', align: 'left', label: 'Preferred Roommate Email 3', field: 'preferredRoommateEmail3',
      },
      {
        name: 'Preferred Roommate Email 4', align: 'left', label: 'Preferred Roommate Email 4', field: 'preferredRoommateEmail4',
      },
      {
        name: 'Preferred Roommate 1', align: 'left', label: 'Preferred Roommate 1', field: 'preferredRoommate1',
      },
      {
        name: 'Preferred Roommate 2', align: 'left', label: 'Preferred Roommate 2', field: 'preferredRoommate2',
      },
      {
        name: 'Preferred Roommate 3', align: 'left', label: 'Preferred Roommate 3', field: 'preferredRoommate3',
      },
      {
        name: 'Preferred Roommate 4', align: 'left', label: 'Preferred Roommate 4', field: 'preferredRoommate4',
      },
      {
        name: 'Returning to Housing (From Previous Session)', align: 'left', label: 'Returning to Housing (From Previous Session)', field: 'isReturningToHousing',
      },
      {
        name: 'Returning for Next Session', align: 'left', label: 'Returning for Next Session', field: 'returningForNextSession',
      },
      {
        name: 'Roommates Same Degree', align: 'left', label: 'Roommates Same Degree', field: 'roommatesHaveSameDegree',
      },
      {
        name: 'Roommates ESA', align: 'left', label: 'Roommates ESA', field: 'roommatesHaveEmotionalSupportAnimal',
      },
      {
        name: 'Room Rate Table', align: 'left', label: 'Room Rate Table', field: 'roomRateTable',
      },
      {
        name: 'Room Name', align: 'left', label: 'Room Name', field: 'roomName',
      },
      {
        name: 'Room Type', align: 'left', label: 'Room Type', field: 'roomType',
      },
      {
        name: 'Complex Name', align: 'left', label: 'Complex Name', field: 'complexName',
      },
      {
        name: 'Gender Unit', align: 'left', label: 'Gender Unit', field: 'genderUnit',
      },
      {
        name: 'Sharing Habits', align: 'left', label: 'Sharing Habits', field: 'sharingHabits',
      },
      {
        name: 'Social Habits', align: 'left', label: 'Social Habits', field: 'socialHabits',
      },
      {
        name: 'Share Email', align: 'left', label: 'Share Email', field: 'shareEmail',
      },
      {
        name: 'Stay In Same Unit', align: 'left', label: 'Stay In Same Unit', field: 'stayInSameUnit',
      },
      {
        name: 'Vehicle Make', align: 'left', label: 'Vehicle Make', field: 'vehicleMake',
      },
      {
        name: 'Vehicle Model', align: 'left', label: 'Vehicle Model', field: 'vehicleModel',
      },
      {
        name: 'Vehicle Color', align: 'left', label: 'Vehicle Color', field: 'vehicleColor',
      },
      {
        name: 'Vehicle State', align: 'left', label: 'Vehicle State', field: 'vehicleState',
      },
      {
        name: 'Vehicle License', align: 'left', label: 'Vehicle License', field: 'vehicleLicense',
      },
      {
        name: 'Proxies', align: 'left', label: 'Proxies', field: 'proxies',
      },
      {
        name: 'NSO Fee Eligible', align: 'left', label: 'NSO Fee Eligible', field: 'nsoFeeEligible',
      },
      {
        name: 'SEA Status', align: 'left', label: 'SEA Status', field: 'seaStatus',
      },
    ];

    const visibleColumns = ref([
      'First Name',
      'Preferred First Name',
      'Last Name',
      'DigiPen Email',
      'Colleague ID',
      'Session Name',
      'Application Status',
      'Waitlist Status',
      'Residency Status',
      'Move In Group',
      'Academic Year',
      'Deposit',
      'Vaccine',
      'DSS',
      'Housing Agreement',
      'Handbook Agreement',
      'Notes',
      'Proxies',
      'NSO Fee Eligible',
    ]);

    const applications = ref();
    const pagination = ref({
      rowsPerPage: 10,
      rowsNumber: -1,
      page: 1,
      sortBy: '',
      descending: false,
    });
    const sessionsDropDown = ref([0]); // 0 is the id for all sessions

    const fetchApplications = async (
      page = 1, rowsPerPage = 10, descending, sortBy, filter, sessionFilter,
    ) => {
      try {
        const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications`; // eslint-disable-line
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('rowsPerPage', rowsPerPage);
        params.append('descending', descending);
        params.append('sortBy', sortBy);
        params.append('filter', filter);
        for (let i = 0; i < sessionFilter.length; i += 1) {
          params.append('sessionFilter[]', sessionFilter[i]);
        }
        for (const [key, value] of Object.entries(columnFilterDict.value)) { // eslint-disable-line
          for (let i = 0; i < value.length; i += 1) {
            params.append(`columnFilter[${key}]`, value[i]);
          }
        }
        const response = await axios.get(url, {
          params,
        });
        const { data } = response;
        applications.value = data.data;
        pagination.value.rowsNumber = data.rowsNumber;
      } catch (error) {
        console.log(error.response);
        notifyResult(error.response.status, error.response);
      }
    };

    // 0 is the id for all sessions
    const updateSessionSelections = () => {
      if (sessionsDropDown.value.length > 1
        && sessionsDropDown.value.includes(0)
        && allSessionsSelected.value) {
        const index = sessionsDropDown.value.indexOf(0);
        sessionsDropDown.value.splice(index, 1);
        allSessionsSelected.value = false;
      } else if (sessionsDropDown.value.length === 0
        || (sessionsDropDown.value.length > 1
          && sessionsDropDown.value.includes(0)
          && !allSessionsSelected.value)
      ) {
        sessionsDropDown.value = [0];
        allSessionsSelected.value = true;
      } else if (!sessionsDropDown.value.includes(0)) {
        allSessionsSelected.value = false;
      }
    };

    const onRequest = async (props) => {
      $q.loading.show();
      const page = props?.pagination?.page ?? pagination.value.page;
      const rowsPerPage = props?.pagination?.rowsPerPage ?? pagination.value.rowsPerPage;
      const sortBy = props?.pagination?.sortBy ?? pagination.value.sortBy;
      const descending = props?.pagination?.descending ?? pagination.value.descending;

      await fetchApplications(
        page,
        rowsPerPage,
        descending,
        sortBy,
        filterModel.value,
        sessionsDropDown.value,
      );
      pagination.value.page = page;
      pagination.value.rowsPerPage = rowsPerPage;
      pagination.value.sortBy = sortBy;
      pagination.value.descending = descending;

      $q.loading.hide();
    };

    const resetPagination = () => {
      pagination.value = {
        rowsPerPage: 10,
        rowsNumber: -1,
        page: 1,
        sortBy: '',
        descending: false,
      };
    };

    const fetchApplicationsBySessions = async () => {
      $q.loading.show();
      updateSessionSelections();
      resetPagination();

      await fetchApplications(
        pagination.value.page,
        pagination.value.rowsPerPage,
        pagination.value.descending,
        pagination.value.sortBy,
        filterModel.value,
        sessionsDropDown.value,
      );

      $q.loading.hide();
    };

    await fetchSessionNameIds(false, true);
    const sessionOptions = computed(() => {
      const names = [];
      for (let i = 0; i < sessionNameIds.value.length; i += 1) {
        names.push({
          label: sessionNameIds.value[i].sessionName,
          value: sessionNameIds.value[i].sessionId,
        });
      }
      return names;
    });
    sessionOptions.value.unshift({ label: 'All', value: 0 });

    const updateApplicationState = (updateApplicationId) => {
      currentApplicationId.value = updateApplicationId;
      selectedRowId.value = updateApplicationId;
    };

    const updateRow = () => {
      for (let i = 0; i < applications.value.length; i += 1) {
        if (applications.value[i].applicationId === selectedRowId.value) {
          applications.value[i] = JSON.parse(JSON.stringify(applicationDetails.value));

          return;
        }
      }
    };

    const submitApplicationForm = async () => {
      $q.loading.show();
      const response = await applicationForm.value.onSubmit();
      if (response === 200 || response === 201) {
        editModal.value.hide();
        await getApplication(selectedRowId.value);
        updateRow();
      }
      $q.loading.hide();
    };

    const closeEmailModal = () => emailModal.value.hide();

    const submitInputFromColumn = async (val, applicationToSubmit, columnName) => {
      $q.loading.show();
      if (columnName === 'DigiPen Email') {
        applicationToSubmit.digipenEmail = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Application Status') {
        applicationToSubmit.status = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Waitlist Status') {
        applicationToSubmit.waitlistStatus = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'DSS') {
        applicationToSubmit.dssAccessible = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Notes') {
        applicationToSubmit.notes = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Deposit Refund Amount') {
        applicationToSubmit.depositRefundAmount = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Residency Status') {
        applicationToSubmit.residencyStatus = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Move In Group') {
        applicationToSubmit.moveInGroup = val; // eslint-disable-line no-param-reassign
      } else if (columnName === 'Returning for Next Session') {
        applicationToSubmit.returningForNextSession = val; // eslint-disable-line no-param-reassign
      } else if (columnName !== 'Housing Agreement'
        && columnName !== 'Handbook Agreement') {
        console.log('column type not handled for update');
      }

      let response = '';
      if (hasRoles('R&D')) {
        response = await updateApplication(applicationToSubmit);
      } else if (hasRoles('ACC')) {
        response = await updateApplicationByAccounting(applicationToSubmit);
      } else {
        response = await updateApplication(applicationToSubmit);
      }
      notifyResult(response.status, response);

      $q.loading.hide();
    };

    const exportApplications = async () => {
      try {
        $q.loading.show();
        let columnsTemp = columns.filter((x) => visibleColumns.value.some((y) => x.name === y));
        columnsTemp = columnsTemp.filter((x) => x.name !== 'All');
        const columnFields = columnsTemp.map((x) => x.field);

        let fileNamePrefix = 'applications_export';
        if (sessionsDropDown.value.length === 1 && sessionsDropDown.value[0] !== 0) {
          const sessionId = sessionsDropDown.value[0];
          const selectedSession = sessionOptions.value.find((s) => s.value === sessionId);
          if (selectedSession) {
            fileNamePrefix = selectedSession.label;
          }
        }

        const result = await getApplicationsExport(
          1,
          pagination.value.rowsPerPage,
          pagination.value.descending,
          pagination.value.sortBy,
          filterModel.value,
          sessionsDropDown.value,
          columnFields,
        );
        notifyResult(result.status, result);
        if (result.status === 200 || result.status === 201) {
          createDownloadableFile(result.data, `${fileNamePrefix} ${formatDate(Date.now())}.csv`, 'text/csv;charset=utf-8;');
        }
      } catch (error) {
        console.log(error);
      }
      $q.loading.hide();
    };

    const importApplications = async () => {
      applicationUploader.value.pickFiles();
    };

    const uploadedApplicationImportData = async () => {
      $q.notify({
        message: 'Successfully Updated',
        color: 'info',
        position: 'top',
        textColor: 'white',
      });
      applicationUploader.value.reset();
      await fetchApplicationsBySessions();
      $q.loading.hide();
    };

    const uploadDataFailed = (info) => {
      $q.notify({
        message: info.xhr.responseText,
        color: 'warning',
        position: 'top',
        textColor: 'white',
      });
      applicationUploader.value.reset();
      $q.loading.hide();
    };

    const selectAccountingFilters = () => {
      visibleColumns.value = [
        'First Name',
        'Preferred First Name',
        'Last Name',
        'DigiPen Email',
        'Colleague ID',
        'Session Name',
        'Deposit',
        'Notes',
        'Deposit Amount',
        'Deposit Completed Date',
        'Deposit Refund Amount',
        'Deposit Refund Notes',
      ];
    };

    const selectPlacementFilters = () => {
      visibleColumns.value = [
        'Application Status',
        'First Name',
        'Preferred First Name',
        'Last Name',
        'DigiPen Email',
        'Colleague ID',
        'Session Name',
        'Room Rate Table',
        'Placement Status',
        'Placement Trait',
        'Move In Group',
        'Residency Status',
        'Returning to Housing (From Previous Session)',
        'Returning for Next Session',
        'Room Name',
        'Room Type',
        'Complex Name',
        'ESA',
      ];
    };

    await fetchApplications(
      pagination.value.page,
      pagination.value.rowsPerPage,
      false,
      null,
      filterModel.value,
      sessionsDropDown.value,
    );

    /* eslint-disable no-param-reassign */
    const changeColumnBoolValues = (val, field) => {
      val = val.map((x) => convertFormattedBooleanColumnToBoolean(field, x));
      columnFilterDict.value[field] = val;
    };
    /* eslint-disable no-param-reassign */

    const applyColumnFilter = async (val, field) => {
      changeColumnBoolValues(val, field);
      await onRequest(null);
    };

    const columnFilterOptions = ref();
    const lazyLoadColumnFilterValues = async (columnField) => {
      $q.loading.show();

      let values = await getColumnFilterValues(columnField, sessionsDropDown.value);

      switch (columnField) {
        case 'dateOfBirth':
        case 'applicationAccepted':
        case 'applicationCreated':
        case 'depositCompleted':
          values = values.map((val) => formatDate(val));
          break;
        default:
          // do nothing
          break;
      }

      columnFilterValues.value = values;
      columnFilterOptions.value = values;
      $q.loading.hide();

      return columnFilterValues;
    };

    const columnFilter = (val, update) => {
      update(() => {
        const needle = val.toLowerCase();
        columnFilterOptions.value = columnFilterValues.value.filter(
          (v) => v.toLowerCase().indexOf(needle) > -1,
        );
      });
    };

    const isEmailModalActive = ref(false);
    const openEmailPreview = async () => {
      try {
        $q.loading.show();
        const result = await getApplicantEmails(
          filterModel.value,
          sessionsDropDown.value,
          columnFilterDict.value,
        );
        if (result && result.status && result.status !== 200) {
          throw new Error(result.data.message || 'Failed to load emails');
        }

        applicantEmailList.value = result;
        isEmailModalActive.value = true;
      } catch (error) {
        $q.notify({
          message: error.message,
          color: 'warning',
          position: 'top',
          textColor: 'white',
        });
      } finally {
        $q.loading.hide();
      }
    };

    const onUpdateToDigiPenEmailsAndEapStatus = async () => {
      if (sessionsDropDown.value.length !== 1 || sessionsDropDown.value[0] === 0) {
        $q.notify({
          message: 'Please select a single session that is also not "All"',
          color: 'warning',
          position: 'top',
          textColor: 'white',
        });
        return;
      }
      await updateToDigiPenEmailsAndEapStatus(sessionsDropDown.value[0]);
      window.location.reload();
    };
    const allColumnsSelected = ref(false);
    const checkAll = (val) => {
      if (val) {
        visibleColumns.value = columns.map((x) => x.name);
      } else {
        visibleColumns.value = [];
      }
    };

    const columnOptions = ref(columns);
    const filterFn = (val, update) => {
      update(() => {
        const needle = val.toLowerCase();
        columnOptions.value = columns.filter((v) => v.label.toLowerCase().indexOf(needle) > -1);
      });
    };

    const openLogsModal = (applicationId) => {
      selectedApplicationForLogs.value = applicationId;
      isLogsModalActive.value = true;
    };

    return {
      q: $q,
      applyColumnFilter,
      allSessionsSelected,
      applicationForm,
      closeEmailModal,
      columnFilter,
      columnFilterDict,
      columnFilterOptions,
      columnFilterValues,
      editModal,
      applicantEmailList,
      emailModal,
      emailPreview,
      hasRoles,
      onRequest,
      columns,
      columnOptions,
      fetchApplicationsBySessions,
      filterModel,
      filterFn,
      lazyLoadColumnFilterValues,
      isEditModalActive: ref(false),
      isEmailModalActive,
      isReadOnlyAccess: ref(false),
      openEmailPreview,
      sessionOptions,
      sessionsDropDown,
      submitApplicationForm,
      submitInputFromColumn,
      pagination,
      applications,
      updateApplicationState,
      exportApplications,
      importApplications,
      visibleColumns,
      applicationUploader,
      uploadDataFailed,
      uploadedApplicationImportData,
      applicationImportUrl,
      selectAccountingFilters,
      selectPlacementFilters,
      onUpdateToDigiPenEmailsAndEapStatus,
      checkAll,
      allColumnsSelected,
      isLogsModalActive,
      selectedApplicationForLogs,
      logsModal,
      openLogsModal,
    };
  },
  components: {
    ApplicationDetailedSummary,
    EmailPreview,
    ApplicationLogs,
  },
};
</script>

<style scoped>
/* Subtle highlight for applicants who are under 18 as of move-in. */
.under-18-row > td {
  background-color: rgba(255, 111, 0, 0.1) !important;
}
.under-18-row > td:first-child {
  box-shadow: inset 3px 0 0 0 #ff6f00;
}
/* Subtle highlight for applicants who are 25 or older as of move-in. */
.over-25-row > td {
  background-color: rgba(63, 81, 181, 0.08) !important;
}
.over-25-row > td:first-child {
  box-shadow: inset 3px 0 0 0 #3f51b5;
}
</style>
